import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { User, RoleInClient, LoginCredentials } from '../types/auth';
import * as authService from '../services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<{ requiresContextSelection: boolean }>;
  selectContext: (clientId: string) => Promise<void>;
  switchContext: (clientId: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isSuperAdmin: () => boolean;
  isAdminClient: () => boolean;
  isAnalyste: () => boolean;
  hasRoleInActiveClient: (role: RoleInClient) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem('auth_token');
    if (!savedToken) {
      setIsLoading(false);
      return;
    }

    try {
      const userData = await authService.getMe();
      setUser(userData);
      setToken(savedToken);
    } catch {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<{ requiresContextSelection: boolean }> => {
    const tokenData = await authService.login(credentials);
    setToken(tokenData.access_token);

    const userData = await authService.getMe();
    setUser(userData);

    const requiresContextSelection = tokenData.requires_context_selection ?? false;
    return { requiresContextSelection };
  };

  const selectContext = useCallback(async (clientId: string) => {
    await authService.selectContext(clientId);
    const userData = await authService.getMe();
    setUser(userData);
    setToken(localStorage.getItem('auth_token'));
  }, []);

  const switchContext = useCallback(async (clientId: string) => {
    await authService.switchContext(clientId);
    const userData = await authService.getMe();
    setUser(userData);
    setToken(localStorage.getItem('auth_token'));
  }, []);

  const logout = useCallback(async () => {
    await authService.logoutBackend();
    setUser(null);
    setToken(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const userData = await authService.getMe();
    setUser(userData);
  }, []);

  const isSuperAdmin = useCallback(() => {
    return user?.system_role === 'superadmin';
  }, [user]);

  const isAdminClient = useCallback(() => {
    return user?.role_in_active_client === 'admin_client' || user?.system_role === 'superadmin';
  }, [user]);

  const isAnalyste = useCallback(() => {
    return user?.role_in_active_client === 'analyste';
  }, [user]);

  const hasRoleInActiveClient = useCallback((role: RoleInClient) => {
    if (!user) return false;
    if (user.system_role === 'superadmin') return true;
    return user.role_in_active_client === role;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        login,
        selectContext,
        switchContext,
        logout,
        refreshUser,
        isSuperAdmin,
        isAdminClient,
        isAnalyste,
        hasRoleInActiveClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
